<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter, type LocationQueryValue } from 'vue-router'
import { useAuthMeta } from '@/composables/useAuthMeta'
import {
  BACK_URL_QUERY_KEY,
  extractRefCodeFromUrl,
  readBackUrlFromLocationSearch,
  resolveNavigationBackUrl,
} from '@/lib/backUrl'
import { isSimpleRegScenario } from '@/lib/simpleRegistration'
import { useInitStore } from '@/stores/initStore'
import { useRegDataStore } from '@/stores/regDataStore'
import Cc3AuthForgotCoralId from '@/app/Cc3AuthForgotCoralId.vue'
import Cc3AuthFullScreenPreloader from '@/app/Cc3AuthFullScreenPreloader.vue'
import Cc3AuthLogo from '@/app/Cc3AuthLogo.vue'
import Cc3AuthPopupAuth from '@/app/Cc3AuthPopupAuth.vue'
import Cc3AuthSimpleFormSkeleton from '@/features/registration/simple/Cc3AuthSimpleFormSkeleton.vue'
import Cc3AuthSimpleHero from '@/features/registration/simple/Cc3AuthSimpleHero.vue'

const route = useRoute()
const router = useRouter()
const initStore = useInitStore()
const regData = useRegDataStore()
const { applyMeta } = useAuthMeta()

const isIndex = computed(() => String(route.name || '').startsWith('index'))
const isError = computed(() => route.name === 'error')
const simpleRegScenario = computed(() =>
  String(route.query.regScenario || regData.regScenario || ''),
)
const isSimpleRegLayout = computed(
  () =>
    isSimpleRegScenario(simpleRegScenario.value) &&
    (route.name === 'registration' || route.name === 'registration-success'),
)
/**
 * Пока init не завершён — не откатываемся с simple-скелетона на центральный,
 * даже если scenario кратковременно мигнёт (например через getConsultantData).
 */
const lockedSimpleRegFormSkeleton = ref(
  isSimpleRegScenario(String(route.query.regScenario || regData.regScenario || '')) &&
    String(route.name || '') === 'registration',
)
const lockedSimpleRegSuccessPreloader = ref(
  isSimpleRegScenario(String(route.query.regScenario || regData.regScenario || '')) &&
    String(route.name || '') === 'registration-success',
)

watch(
  [simpleRegScenario, () => route.name, () => initStore.loaded],
  ([scenario, name, loaded]) => {
    if (loaded) return
    if (name === 'registration' && isSimpleRegScenario(scenario)) {
      lockedSimpleRegFormSkeleton.value = true
    }
    if (name === 'registration-success' && isSimpleRegScenario(scenario)) {
      lockedSimpleRegSuccessPreloader.value = true
    }
  },
)

/** Entry: show hero + form skeleton while init loads. */
const isSimpleRegFormPending = computed(
  () => lockedSimpleRegFormSkeleton.value && route.name === 'registration',
)
/** After auto-login hard nav to success: centered coral.club spinner. */
const isSimpleRegSuccessPending = computed(
  () =>
    lockedSimpleRegSuccessPreloader.value &&
    route.name === 'registration-success',
)

const logoClass = computed(() =>
  isIndex.value
    ? 'left-1/2 -translate-x-1/2'
    : 'left-0',
)

function firstQueryValue(
  value: LocationQueryValue | LocationQueryValue[],
): string | null {
  const raw = Array.isArray(value) ? value[0] : value
  return typeof raw === 'string' && raw.length ? raw : null
}

function readBackUrlQuery(): string | null {
  const fromRoute = firstQueryValue(route.query[BACK_URL_QUERY_KEY])
  if (fromRoute) return fromRoute
  return readBackUrlFromLocationSearch()
}

let overflowTimer: ReturnType<typeof setTimeout> | null = null

watch(isIndex, () => {
  document.body.style.overflow = 'hidden'
  if (overflowTimer) clearTimeout(overflowTimer)
  overflowTimer = setTimeout(() => {
    document.body.style.overflow = ''
  }, 2000)
})

onUnmounted(() => {
  if (overflowTimer) clearTimeout(overflowTimer)
  document.body.style.overflow = ''
})

onMounted(async () => {
  const query = route.query
  const backUrlFromQuery = readBackUrlQuery()
  initStore.resolveBackUrlFromContext(backUrlFromQuery)

  const backForBar =
    backUrlFromQuery || resolveNavigationBackUrl(backUrlFromQuery)

  if (backForBar && !query[BACK_URL_QUERY_KEY]) {
    await router.replace({
      query: { ...query, [BACK_URL_QUERY_KEY]: backForBar },
    })
  } else if (backUrlFromQuery) {
    const encodedParam = `${BACK_URL_QUERY_KEY}=${encodeURIComponent(backUrlFromQuery)}`
    if (!window.location.search.includes(encodedParam)) {
      await router.replace({
        query: { ...query, [BACK_URL_QUERY_KEY]: backUrlFromQuery },
      })
    }
  }

  if (typeof query.memberId === 'string') {
    regData.setMemberId(query.memberId)
  }
  // Реф из BACK_URL (корзина) важнее query/cookie — там актуальный код для реги.
  const refFromBackUrl = extractRefCodeFromUrl(backUrlFromQuery)
  const refFromQuery =
    typeof query.REF_CODE === 'string' ? query.REF_CODE : null
  const refCode = refFromBackUrl || refFromQuery
  if (refCode) {
    if (refFromBackUrl && refFromBackUrl !== refFromQuery) {
      await router.replace({
        query: { ...route.query, REF_CODE: refFromBackUrl },
      })
    }
    await regData.setRefCodeReg(refCode)
  }

  await initStore.fetchInitialData()
  applyMeta()
})

watch(
  () => firstQueryValue(route.query[BACK_URL_QUERY_KEY]),
  (value) => {
    if (value) initStore.resolveBackUrlFromContext(value)
  },
)
</script>

<template lang="pug">
.auth-shell
  template(v-if="!initStore.loaded")
    .login-bg(v-if="isSimpleRegFormPending" class="login simple-reg-layout")
      Cc3AuthSimpleHero
      .allOtherPages.wrap.active.simple-reg-panel
        Cc3AuthSimpleFormSkeleton
    Cc3AuthFullScreenPreloader(v-else-if="isSimpleRegSuccessPending")
    .skeleton-main(v-else class="login items-center justify-center bg-gray-100")
      C2Skeleton
  RouterView(v-else-if="isError")
  .login-bg(v-else class="login" :class="{ 'simple-reg-layout': isSimpleRegLayout }")
    Transition(name="slide-left")
      Cc3AuthSimpleHero(v-if="isSimpleRegLayout")
    .logo-slot(
      v-if="!isSimpleRegLayout"
      class="relative lg:h-10 w-full opacity-0 lg:opacity-100",
      :class="{ 'hidden lg:block': !isIndex }",
    )
      Cc3AuthLogo.logo(
        dark,
        class="logo transition-all ease-in duration-300 absolute top-1/2 -translate-y-1/2",
        :class="logoClass",
      )
    Transition(name="slide" mode="out-in")
      .allOtherPages.wrap(v-if="!isIndex" key="other" :class="{ active: !isIndex, 'simple-reg-panel': isSimpleRegLayout }")
        RouterView
    Transition(name="scale" mode="out-in")
      .mainPage(v-if="isIndex" key="main")
        Cc3AuthPopupAuth(:is-index="true")
          RouterView
        Transition(name="fade-opacity")
          Cc3AuthForgotCoralId(v-if="initStore.forgotCoralIdPopup")
</template>

<style scoped>
.auth-shell {
  display: contents;
}
</style>
